import React, { useMemo } from 'react';
import { formatCaseDateTime } from '../utils/formatCaseDate';
import {
  CASE_STATUSES, CASE_PRIORITIES, CASE_TYPES,
  statusLabel, priorityLabel, typeLabel,
} from '../constants/supportCases';
import Avatar from './Avatar';
import AttachmentGrid from './AttachmentGrid';
import './CaseTimeline.css';

// Map either a raw code ("in_progress") or a display label ("In progress") back to the
// canonical code, so historical events (stored as codes) and new events (stored as labels)
// both resolve to the same colored pill.
const codeLookup = (list) => {
  const m = {};
  list.forEach((o) => { m[o.value.toLowerCase()] = o.value; m[o.label.toLowerCase()] = o.value; });
  return m;
};
const STATUS_CODES = codeLookup(CASE_STATUSES);
const PRIORITY_CODES = codeLookup(CASE_PRIORITIES);
const TYPE_CODES = codeLookup(CASE_TYPES);

// Pull the two sides out of a "Label: a → b" (or bare "a → b") detail string.
const parseTransition = (detail) => {
  if (!detail) return null;
  const i = detail.indexOf(':');
  const body = i >= 0 ? detail.slice(i + 1) : detail;
  const parts = body.split('→').map((s) => s.trim()).filter(Boolean);
  return parts.length === 2 ? parts : null;
};

const StatusChip = ({ token }) => {
  const code = STATUS_CODES[token.toLowerCase()];
  return code
    ? <span className={`support-case-status status-${code}`}>{statusLabel(code)}</span>
    : <span className="case-timeline-chip">{token}</span>;
};
const PriorityChip = ({ token }) => {
  const code = PRIORITY_CODES[token.toLowerCase()];
  return code
    ? <span className={`case-priority priority-${code}`}>{priorityLabel(code)}</span>
    : <span className="case-timeline-chip">{/^unset$/i.test(token) ? 'Unset' : token}</span>;
};
const typeText = (token) => typeLabel(TYPE_CODES[token.toLowerCase()] || token);

/**
 * A single cohesive case timeline: public comments (and, for agents, internal notes)
 * merged chronologically with field-change activity events (status, type, priority,
 * assignment, etc.). Comments render as rich cards; events render as compact rows.
 *
 * Comments and events are fetched separately and passed in. What an end user is allowed
 * to see is enforced server-side (the tenant timeline endpoint filters the event types
 * and never returns internal notes), so this component just renders what it is given.
 */
function CommentItem({ caseId, cm, fetchBlob, showInternal }) {
  const author = cm.author_name || cm.author_username || 'Unknown';
  const internal = showInternal && cm.is_internal;
  return (
    <div className={`case-timeline-item case-timeline-comment${internal ? ' is-internal' : ''}`}>
      <span className="case-timeline-marker case-timeline-marker--comment" aria-hidden />
      <div className="support-case-comment">
        <div className="support-case-comment-head">
          <span className="support-case-comment-author">
            <Avatar userId={cm.author_user_id} name={author} size={24} />
            <span className="support-case-comment-author-name">{author}</span>
            {internal && <span className="support-case-internal-badge">Internal</span>}
          </span>
          <span className="support-case-comment-time">{formatCaseDateTime(cm.created_at)}</span>
        </div>
        <div className="support-case-comment-body">
          {String(cm.body || '').split('\n').map((line, i) => <p key={i}>{line || ' '}</p>)}
        </div>
        {cm.attachments?.length > 0 && (
          <AttachmentGrid caseId={caseId} attachments={cm.attachments} fetchBlob={fetchBlob} compact />
        )}
      </div>
    </div>
  );
}

function EventItem({ ev }) {
  const actor = ev.actor_name || ev.actor_username || 'System';
  const pair = ['status_changed', 'priority_changed', 'type_changed'].includes(ev.event_type)
    ? parseTransition(ev.detail)
    : null;

  let body;
  if (pair && ev.event_type === 'status_changed') {
    body = (
      <>
        <span className="case-timeline-event-kind">Status</span>
        <StatusChip token={pair[0]} />
        <span className="case-timeline-arrow">→</span>
        <StatusChip token={pair[1]} />
      </>
    );
  } else if (pair && ev.event_type === 'priority_changed') {
    body = (
      <>
        <span className="case-timeline-event-kind">Priority</span>
        <PriorityChip token={pair[0]} />
        <span className="case-timeline-arrow">→</span>
        <PriorityChip token={pair[1]} />
      </>
    );
  } else if (pair && ev.event_type === 'type_changed') {
    body = (
      <>
        <span className="case-timeline-event-kind">Type</span>
        <strong>{typeText(pair[0])}</strong>
        <span className="case-timeline-arrow">→</span>
        <strong>{typeText(pair[1])}</strong>
      </>
    );
  } else {
    body = <span className="case-timeline-event-detail">{ev.detail}</span>;
  }

  return (
    <div className="case-timeline-item case-timeline-event">
      <span className={`case-timeline-marker case-timeline-marker--${ev.event_type}`} aria-hidden />
      <div className="case-timeline-event-body">
        <span className="case-timeline-event-line">
          <strong className="case-timeline-actor">{actor}</strong>
          {body}
        </span>
        <span className="case-timeline-event-time">{formatCaseDateTime(ev.created_at)}</span>
      </div>
    </div>
  );
}

function CaseTimeline({ caseId, comments = [], events = [], fetchBlob, showInternal = false }) {
  const items = useMemo(() => {
    const merged = [
      ...comments.map((cm) => ({ kind: 'comment', at: cm.created_at, key: `c-${cm.id}`, data: cm })),
      ...events.map((ev) => ({ kind: 'event', at: ev.created_at, key: `e-${ev.id}`, data: ev })),
    ];
    // Chronological (oldest first), matching the previous comment-thread order.
    return merged.sort((a, b) => new Date(a.at) - new Date(b.at));
  }, [comments, events]);

  if (items.length === 0) {
    return <p className="support-case-thread-empty">No activity yet.</p>;
  }

  return (
    <div className="case-timeline">
      {items.map((it) =>
        it.kind === 'comment'
          ? <CommentItem key={it.key} caseId={caseId} cm={it.data} fetchBlob={fetchBlob} showInternal={showInternal} />
          : <EventItem key={it.key} ev={it.data} />
      )}
    </div>
  );
}

export default CaseTimeline;
